import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Loader2, XCircle, RefreshCw, AlertCircle, CreditCard, CheckCircle, AlertTriangle, Clock, Send } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from 'sonner';
import { useProfile } from '../../contexts/ProfileContext';
import { supabase } from '../../lib/supabase';

// Removed unused currentSubscription object

const CountdownTimer = ({ expiryTime }: { expiryTime: string }) => {
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiryTime).getTime();
      const difference = expiry - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft('Expired');
        return;
      }

      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [expiryTime]);

  return (
    <span className={isExpired ? "text-red-400 font-bold" : "text-yellow-400 font-mono font-bold"}>
      {timeLeft}
    </span>
  );
};

export function SubscriptionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, refreshProfile } = useProfile();
  const [selectedPlan] = useState('12_months');
  const [loading, setLoading] = useState(false);
  const [isFetchingStatus, setIsFetchingStatus] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
  // const [paymentMethods] = useState<any[]>([]); // Removed unused variable
  // const [transactions] = useState<any[]>([]); // Removed unused variable
  const [hasActivePayment, setHasActivePayment] = useState(false);
  const [pendingTransactions, setPendingTransactions] = useState<any[]>([]);
  const [hasScrolledToPending, setHasScrolledToPending] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [showChangePlanDialog, setShowChangePlanDialog] = useState(false);
  const [selectedChangePlan, setSelectedChangePlan] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);

  // Release Timer Logic
  const RELEASE_DATE = "2026-06-29T09:00:00+07:00"; // Senin, 29 Juni 2026 09:00 WIB
  const [isReleased, setIsReleased] = useState(false);
  const [releaseTimeLeft, setReleaseTimeLeft] = useState<string>('');

  useEffect(() => {
    const checkRelease = () => {
      console.log("Current VITE_TEST_SUBSCRIPTION value:", (import.meta as any).env.VITE_TEST_SUBSCRIPTION);
      // Test mode bypass
      if ((import.meta as any).env.VITE_TEST_SUBSCRIPTION === 'true' || (import.meta as any).env.VITE_TEST_SUBSCRIPTION === true) {
        setIsReleased(true);
        return;
      }

      const now = new Date().getTime();
      const release = new Date(RELEASE_DATE).getTime();
      const difference = release - now;

      if (difference <= 0) {
        setIsReleased(true);
      } else {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        let timeString = '';
        if (days > 0) timeString += `${days}d `;
        timeString += `${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
        setReleaseTimeLeft(timeString);
      }
    };

    checkRelease();
    const timer = setInterval(checkRelease, 1000);
    return () => clearInterval(timer);
  }, []);

  // Helper function for image error handling
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.style.display = 'none';
    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
    if (fallback) {
      fallback.style.display = 'inline';
    }
  };

  // Helper function to get Supabase URL
  const getSupabaseUrl = () => {
    return (import.meta as any).env?.VITE_SUPABASE_URL || 'https://bandarmolony.supabase.co';
  };

  // Helper function to get payment logo URL
  const getPaymentLogoUrl = (logoFile: string) => {
    return `${getSupabaseUrl()}/storage/v1/object/public/assets/images/pay_logo/${logoFile}`;
  };

  // Helper function to get display name from logo file
  const getLogoDisplayName = (logoFile: string) => {
    return logoFile.replace('logo_', '').replace('.png', '').toUpperCase();
  };

  // Helper function to get payment method display name
  const getPaymentMethodDisplayName = (paymentMethod: string) => {
    if (!paymentMethod || paymentMethod === 'snap') return '-';

    // Backend formats ALL payment methods correctly from Midtrans webhook data
    // Just display it as-is
    return paymentMethod;
  };



  // Auto scroll to pending payment on first load
  // BUT skip if user just navigated to this page (to prioritize Current Subscription section)
  useEffect(() => {
    // Only auto-scroll to pending if data was already loaded before navigation
    // This prevents overriding the scroll to Current Subscription section
    if (pendingTransactions.length > 0 && !hasScrolledToPending && subscriptionStatus !== null) {
      // Add longer delay to ensure scroll to top happens first
      const timer = setTimeout(() => {
        const pendingSection = document.getElementById('pending-transactions');
        if (pendingSection) {
          pendingSection.scrollIntoView({ behavior: 'smooth' });
          setHasScrolledToPending(true);
        }
      }, 1500); // Increased delay to allow scroll to top/Current Subscription first
      return () => clearTimeout(timer);
    }
    return undefined; // Explicit return for all code paths
  }, [pendingTransactions.length, hasScrolledToPending, subscriptionStatus]);

  // Payment methods configuration
  const paymentMethodsConfig = {
    qris: {
      title: 'QRIS',
      logos: ['logo_qris.png']
    },
    ewallet: {
      title: 'E-wallet',
      logos: ['logo_gopay.png', 'logo_spay.png', 'logo_dana.png']
    },
    virtualAccount: {
      title: 'Virtual Account',
      logos: ['logo_bca.png', 'logo_bni.png', 'logo_pb.png']
    },
    creditCard: {
      title: 'Credit/Debit Card',
      logos: ['logo_visa.png', 'logo_mastercard.png', 'logo_jcb.png', 'logo_amex.png']
    }
    // alfaGroup: {
    //   title: 'Alfa Group',
    //   logos: ['logo_alfamart.png', 'logo_alfamidi.png', 'logo_dandan.png']
    // },
    // indomaret: {
    //   title: 'Indomaret',
    //   logos: ['logo_indomaret.png', 'logo_isaku.png']
    // }
  };

  // PaymentLogo component
  const PaymentLogo = ({ logoFile }: { logoFile: string }) => (
    <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200 w-16 h-16 flex items-center justify-center">
      <img
        src={getPaymentLogoUrl(logoFile)}
        alt={getLogoDisplayName(logoFile)}
        className="w-12 h-12 object-contain"
        onError={handleImageError}
      />
    </div>
  );

  // PaymentGroup component
  const PaymentGroup = ({ title, logos }: { title: string; logos: string[] }) => (
    <div className="flex-shrink-0 border border-gray-200 rounded-lg p-2 pb-1.5">
      <h4 className="text-xs font-semibold mb-1 text-center text-white">{title}</h4>
      <div className="flex gap-2">
        {logos.map((logoFile, index) => (
          <PaymentLogo key={index} logoFile={logoFile} />
        ))}
      </div>
    </div>
  );

  // Scroll to top when navigating to this page
  // This works for both initial mount and navigation from other pages
  useEffect(() => {
    // Reset scroll flag when navigating to ensure clean scroll behavior
    setHasScrolledToPending(false);

    // Use setTimeout to ensure DOM is ready after navigation
    const timer = setTimeout(() => {
      // Try to scroll to Current Subscription section first, otherwise scroll to top
      const currentSubscriptionSection = document.getElementById('current-subscription');
      if (currentSubscriptionSection) {
        currentSubscriptionSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100); // Slight delay to ensure all components are rendered

    return () => clearTimeout(timer);
  }, [location.pathname]); // Trigger on pathname change

  const loadPlans = async () => {
    try {
      const response = await api.getSubscriptionPlans();
      if (response.success && Array.isArray(response.data)) {
        setPlans(response.data);
      }
    } catch (error) {
      console.error('Failed to load subscription plans:', error);
    }
  };

  useEffect(() => {
    // Load data sequentially to prevent race conditions
    const loadData = async () => {
      try {
        await loadPlans();
        await loadSubscriptionStatus();
      } catch (error) {
        console.error('Error loading subscription data:', error);
      }
    };

    loadData();

    // Removed unconditional auto-refresh. We now use a conditional refresh below.
  }, []);

  // Smart Polling: Only refresh every 15 seconds if the user has an active pending payment
  useEffect(() => {
    if (!hasActivePayment) return;

    const interval = setInterval(() => {
      loadSubscriptionStatus();
    }, 15000);

    return () => clearInterval(interval);
  }, [hasActivePayment]);

  const loadSubscriptionStatus = async () => {
    try {
      setIsFetchingStatus(true);
      const response = await api.getSubscriptionStatus();
      if (response.success) {
        setSubscriptionStatus(response.data);
        // setTransactions(response.data.transactions || []); // Removed unused setter

        // Debug logging
        console.log('Subscription data:', response.data);
        console.log('Transactions:', response.data.transactions);

        // Check if there's an active payment (only pending, not settlement or cancel)
        const hasPendingPayment = response.data.transactions?.some(
          (tx: any) => tx.status === 'pending'
        );
        setHasActivePayment(hasPendingPayment || false);

        // Load pending transactions (only truly pending ones, exclude cancelled and expired)
        const pendingTxs = response.data.transactions?.filter(
          (tx: any) => {
            if (tx.status !== 'pending') return false;

            // Check if transaction is not expired
            const now = new Date();
            const expiryTime = new Date(tx.expiry_time);
            return now <= expiryTime;
          }
        ) || [];
        setPendingTransactions(pendingTxs);

        // Reset scroll flag when new pending transactions are loaded
        if (pendingTxs.length > 0) {
          setHasScrolledToPending(false);
        }

        // If backend says subscription is active but our global profile context still says otherwise,
        // force a profile refresh to sync the state across the app so NotSubscribed component disappears.
        if (response.data.status === 'active' && profile?.subscriptionStatus !== 'active') {
          console.log('Subscription active detected! Forcing profile refresh...');
          refreshProfile(true);
        }

        console.log('Has pending payment:', hasPendingPayment);
        console.log('Pending transactions:', pendingTxs);
      }
    } catch (error) {
      console.error('Failed to load subscription status:', error);
    } finally {
      setIsFetchingStatus(false);
    }
  };



  const getSubscriptionStatus = () => {
    if (!subscriptionStatus?.active_subscriptions || subscriptionStatus.active_subscriptions.length === 0) {
      if (!subscriptionStatus?.subscription) {
        return { status: 'inactive', daysLeft: 0, isActive: false, planName: 'Free Plan', isCancelled: false };
      }
    }

    const subs = subscriptionStatus?.active_subscriptions && subscriptionStatus.active_subscriptions.length > 0
      ? subscriptionStatus.active_subscriptions
      : (subscriptionStatus?.subscription ? [subscriptionStatus.subscription] : []);

    if (subs.length === 0) {
      return { status: 'inactive', daysLeft: 0, isActive: false, planName: 'Free Plan', isCancelled: false };
    }

    const activeSubs = subs.filter((s: any) => !['failed', 'expired', 'cancelled'].includes(s.status));
    if (activeSubs.length === 0) {
      return { status: 'inactive', daysLeft: 0, isActive: false, planName: 'Free Plan', isCancelled: false };
    }

    const subscription = activeSubs[0];
    const combinedPlanName = activeSubs.map((s: any) => s.plan_name).join(' + ');

    const now = new Date();
    const endDate = new Date(subscription.end_date);
    const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    const isActive = (subscription.status === 'active' || subscription.status === 'trial') && daysLeft > 0;
    const isCancelled = !!subscription.cancelled_at || subscription.auto_renew === false;

    return {
      status: subscription.status,
      daysLeft: Math.max(0, daysLeft),
      isActive,
      isCancelled,
      endDate: subscription.end_date,
      planName: combinedPlanName,
      planId: subscription.plan_id
    };
  };

  const formatDuration = (days: number) => {
    if (days >= 30) {
      const months = Math.round(days / 30);
      return `${months} month${months !== 1 ? 's' : ''}`;
    }
    return `${days} day${days !== 1 ? 's' : ''}`;
  };

  // Function to update payment method
  const updatePaymentMethod = async (transactionId: string, paymentType: string) => {
    try {
      await api.updatePaymentMethod({ transactionId, paymentMethod: paymentType });
    } catch (error) {
      console.error('Failed to update payment method:', error);
    }
  };

  const handleSubscribe = async (planId: string) => {
    // Check if there are pending transactions
    if (hasActivePayment) {
      toast.error('Anda memiliki pembayaran yang sedang berlangsung. Silakan tunggu hingga selesai.');
      return;
    }

    try {
      setLoading(true);
      setLoadingPlan(planId);

      // Prioritize the user's permanent referral code from DB, fallback to localStorage
      const referralCode = profile?.referred_by_code || localStorage.getItem('referral_code');

      if (referralCode) {
        try {
          // @ts-ignore
          const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
          let sessionId = localStorage.getItem('referral_session_id');
          const { data: { session } } = await supabase.auth.getSession();
          const headers: HeadersInit = { 'Content-Type': 'application/json' };
          if (session?.access_token) {
            headers['Authorization'] = `Bearer ${session.access_token}`;
          }

          await fetch(`${API_URL}/api/referral/track`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ code: referralCode, event_type: 'CHECKOUT', session_id: sessionId, subscription_plan_id: planId })
          });
        } catch (error) {
          console.error('Failed to track referral claim:', error);
        }
      }
      const payload: { planId: string; paymentMethod: string; referralCode?: string } = {
        planId,
        paymentMethod: 'snap' // Snap adalah gateway, payment method akan dipilih user di popup
      };

      if (referralCode) {
        payload.referralCode = referralCode;
      }

      const response = await api.createSubscriptionOrder(payload);

      if (response.success) {
        // Initialize Midtrans Snap
        if ((window as any).snap) {
          (window as any).snap.pay(response.data.snapToken, {
            onSuccess: (result: any) => {
              console.log('Payment success:', result);
              // Update payment method based on result
              if (result.payment_type) {
                updatePaymentMethod(response.data.transactionId, result.payment_type);
              }
              toast.success('Payment berhasil! Subscription Anda telah diaktifkan.');
              setHasActivePayment(false);
              // Refresh status and stay on subscription page
              setTimeout(() => {
                loadSubscriptionStatus();
                // Scroll to top to show updated subscription status
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }, 1000);
            },
            onPending: (result: any) => {
              console.log('Payment pending:', result);
              // Update payment method based on result
              if (result.payment_type) {
                updatePaymentMethod(response.data.transactionId, result.payment_type);
              }
              toast.info('Payment sedang diproses. Silakan tunggu konfirmasi.');
              setHasActivePayment(true);
              // Refresh status with delay to prevent race conditions
              setTimeout(() => {
                loadSubscriptionStatus();
              }, 1000);
            },
            onError: (result: any) => {
              console.log('Payment error:', result);
              toast.error('Payment gagal. Silakan coba lagi.');
            },
            onClose: () => {
              console.log('Payment popup closed');
              setHasActivePayment(false);
              // Just refresh status without full page reload
              loadSubscriptionStatus();
            }
          });
        } else {
          toast.error('Payment gateway tidak tersedia. Silakan refresh halaman.');
        }
      } else {
        toast.error(response.error || 'Gagal membuat order pembayaran');
      }
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Terjadi kesalahan saat memproses subscription');
    } finally {
      setLoading(false);
      setLoadingPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    // Validate confirmation text
    if (cancelConfirmText !== 'Cancel') {
      toast.error('Mohon ketik "Cancel" untuk konfirmasi');
      return;
    }

    try {
      setLoading(true);
      setShowCancelDialog(false);
      const response = await api.cancelSubscription({
        reason: 'User requested cancellation'
      });

      if (response.success) {
        toast.success('Subscription akan berhenti setelah periode berakhir');
        setCancelConfirmText(''); // Reset
        loadSubscriptionStatus();
      } else {
        toast.error(response.error || 'Gagal membatalkan subscription');
      }
    } catch (error: any) {
      console.error('Cancel subscription error:', error);
      toast.error(error.message || 'Terjadi kesalahan saat membatalkan subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleContinuePayment = async (transaction: any) => {
    try {
      setLoading(true);

      // Always regenerate snap token to ensure it's fresh and valid
      toast.info('Mempersiapkan halaman pembayaran...');

      const response = await api.regenerateSnapToken({
        transactionId: transaction.id
      });

      if (response.success) {
        // Open payment URL in new tab for pending payments
        if (response.data.paymentUrl) {
          window.open(response.data.paymentUrl, '_blank');
          toast.success('Halaman pembayaran dibuka di tab baru. Silakan selesaikan pembayaran Anda.');
          // Refresh status after a short delay
          setTimeout(() => {
            loadSubscriptionStatus();
          }, 3000);
        } else {
          toast.error('URL pembayaran tidak tersedia. Silakan coba lagi.');
        }
      } else {
        // Handle specific error cases
        if (response.error?.includes('expired')) {
          toast.error('Transaksi telah expired. Silakan buat order baru.');
          // Refresh data to remove expired transaction
          loadSubscriptionStatus();
        } else {
          toast.error(response.error || 'Gagal mempersiapkan halaman pembayaran. Silakan coba lagi.');
        }
      }
    } catch (error: any) {
      console.error('Continue payment error:', error);
      toast.error('Terjadi kesalahan saat mempersiapkan pembayaran. Silakan coba lagi atau hubungi support.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTransaction = async (transaction: any) => {
    if (!transaction.payment_method || transaction.payment_method === 'snap') {
      toast.error('Silakan klik "Pilih Metode Bayar" dan pilih metode pembayaran terlebih dahulu sebelum membatalkan transaksi.');
      return;
    }

    try {
      setLoading(true);

      // Cancel the pending transaction
      const response = await api.cancelPendingTransaction({
        transactionId: transaction.id,
        reason: 'User cancelled pending transaction'
      });

      if (response.success) {
        toast.success('Transaksi berhasil dibatalkan');
        // Force refresh all data
        await Promise.all([
          loadSubscriptionStatus()
        ]);
        // Reset states
        setHasActivePayment(false);
        setPendingTransactions([]);
      } else {
        toast.error(response.error || 'Gagal membatalkan transaksi');
      }
    } catch (error: any) {
      console.error('Cancel transaction error:', error);
      toast.error(error.message || 'Terjadi kesalahan saat membatalkan transaksi');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="h-[calc(100vh-60px)] flex flex-col space-y-1 p-1 lg:p-1 overflow-y-auto overflow-x-hidden">
      {/* Pending Transaction Section */}
      {pendingTransactions.length > 0 && (
        <Card id="pending-transactions" className="border-orange-500 bg-orange-500/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-orange-100 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Transaksi Pending
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  loadSubscriptionStatus();
                  toast.info('Refreshing transaction status...');
                }}
                disabled={loading}
                className="border-orange-500/30 hover:bg-orange-500/20"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Alert className="bg-blue-500/10 border-blue-500/20 text-blue-100">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Anda tidak dapat membuka transaksi baru karena ada transaksi yang belum dibayar.
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <h4 className="font-medium text-orange-100">Berikut transaksi yang sedang pending:</h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-orange-500/20">
                        <th className="text-left py-2 px-3 text-orange-200 text-sm">Date</th>
                        <th className="text-left py-2 px-3 text-orange-200 text-sm">Status</th>
                        <th className="text-left py-2 px-3 text-orange-200 text-sm">Time Left</th>
                        <th className="text-left py-2 px-3 text-orange-200 text-sm">Amount</th>
                        <th className="text-left py-2 px-3 text-orange-200 text-sm">Product</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingTransactions.map((transaction) => (
                        <tr key={transaction.id} className="border-b border-orange-500/10">
                          <td className="py-2 px-3 text-orange-100 text-sm">
                            {formatDate(transaction.created_at)}
                          </td>
                          <td className="py-2 px-3">
                            <Badge className="bg-blue-500 text-white text-xs">
                              UNPAID
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-sm">
                            <CountdownTimer expiryTime={transaction.expiry_time} />
                          </td>
                          <td className="py-2 px-3 text-orange-100 text-sm font-medium">
                            IDR {transaction.amount?.toLocaleString() || '0'}
                          </td>
                          <td className="py-2 px-3 text-orange-100 text-sm">
                            {transaction.subscriptions?.plan_name || 'Unknown Plan'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="pt-4 border-t border-orange-500/20">
                <div className="text-center">
                  <p className="text-orange-100 mb-4">
                    Mau lanjut bayar, atau di cancel saja?
                  </p>
                  <div className="flex gap-3 justify-center">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white px-6"
                      onClick={() => handleContinuePayment(pendingTransactions[0])}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          {pendingTransactions[0]?.payment_method && pendingTransactions[0].payment_method !== 'snap'
                            ? 'Lanjut Bayar'
                            : 'Pilih Metode Bayar'
                          }
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="px-6"
                      onClick={() => handleCancelTransaction(pendingTransactions[0])}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancel Transaction
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <Alert className="bg-orange-500/10 border-orange-500/20 text-orange-100">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1 text-sm">
                    {(pendingTransactions[0]?.payment_method && pendingTransactions[0].payment_method !== 'snap') ? (
                      <p>Metode pembayaran sudah dipilih: <strong>{getPaymentMethodDisplayName(pendingTransactions[0].payment_method)}</strong></p>
                    ) : (
                      <>
                        <p>⚠️ Transaksi pending belum memilih metode pembayaran.</p>
                        <p>Klik <strong>"Pilih Metode Bayar"</strong> untuk memilih cara pembayaran (misal Bank Transfer, E-Wallet), atau klik <strong>"Cancel Transaksi"</strong> jika ingin mengganti paket.</p>
                      </>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Subscription Status */}
      <Card id="current-subscription">
        <CardContent className="pt-2 pb-2 px-4 [&:last-child]:pb-2 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-muted-foreground">Current Subscription:</span>

            {(() => {
              if (isFetchingStatus) {
                return (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    <span className="text-sm font-semibold text-muted-foreground">Loading...</span>
                  </div>
                );
              }

              const subStatus = getSubscriptionStatus();

              if (!subscriptionStatus?.subscription || !subStatus.isActive) {
                return (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">Free Plan</span>
                    <Badge variant="outline" className="font-normal text-[11px] py-0 h-5 text-muted-foreground">Valid until: Selamanya</Badge>
                  </div>
                );
              }

              return (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">{subStatus.planName}</span>
                  <Badge className={
                    subStatus.isActive
                      ? 'bg-green-100 text-green-800 border-green-200 py-0 h-5 px-2 text-[10px]'
                      : subStatus.status === 'expired'
                        ? 'bg-red-100 text-red-800 border-red-200 py-0 h-5 px-2 text-[10px]'
                        : 'bg-yellow-100 text-yellow-800 border-yellow-200 py-0 h-5 px-2 text-[10px]'
                  }>
                    {subStatus.isActive ? 'Active' : subStatus.status}
                  </Badge>
                  <Badge variant="outline" className="font-normal text-[11px] py-0 h-5 text-muted-foreground">
                    {subStatus.isActive
                      ? `Valid until: ${new Date(subStatus.endDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}`
                      : subStatus.status === 'expired'
                        ? 'Subscription expired'
                        : 'Subscription inactive'
                    }
                  </Badge>

                  {subscriptionStatus?.subscription?.cancelled_at && subStatus.isActive && (
                    <span className="text-xs text-yellow-600 dark:text-yellow-400 ml-1">
                      ⚠️ Cancels on {new Date(subStatus.endDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="flex items-center gap-2 shrink-0 ml-4">
            {(() => {
              const subStatus = getSubscriptionStatus();
              if (subStatus.isActive && !subscriptionStatus?.subscription?.cancelled_at) {
                return (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={() => setShowCancelDialog(true)}
                    disabled={loading}
                  >
                    Cancel Active Plan
                  </Button>
                );
              }
              return null;
            })()}
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => navigate('/subscription/history')}
              disabled={loading}
            >
              <CreditCard className="w-3 h-3 mr-1" />
              Payment History
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Plans */}
      <Card id="subscription-plans">
        <CardHeader className="pt-4 pb-3 px-4 !gap-0.5">
          <CardTitle className="text-base text-center">Choose Your Plan</CardTitle>
          <p className="text-xs text-muted-foreground text-center !mt-0">
            {isReleased
              ? "All plans grant full access to all features. Choose what works best for you."
              : "Subscription plans will be available shortly. Stay tuned!"}
          </p>
          {!isReleased && (
            <div className="flex justify-center mt-2">
              <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20 px-3 py-1">
                <AlertTriangle className="w-3.5 h-3.5 mr-1.5 inline-block" />
                Available Monday, 29 June 2026 at 09:00 WIB
              </Badge>
            </div>
          )}
        </CardHeader>
        <CardContent className="px-4 pb-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-200 flex flex-col h-full !gap-2 ${plan.popular ? 'ring-1 ring-primary shadow-sm bg-primary/5' : ''} ${selectedPlan === plan.id ? 'border-primary' : ''
                  }`}
              >
                {plan.popular && (
                  <div className="absolute right-[-40px] top-[20px] w-[150px] rotate-45 bg-yellow-400 py-1 text-center text-xs font-bold text-black shadow-sm z-10 tracking-wider">
                    BEST VALUE
                  </div>
                )}
                <CardHeader className="pt-3 pb-2 px-3 flex flex-col items-center">
                  <div className="flex items-center gap-2 justify-center">
                    <CardTitle className="text-center text-base">{plan.name}</CardTitle>
                  </div>
                  <div className="text-center mt-1">
                    <span className="text-xl font-bold">
                      Rp {plan.price.toLocaleString()}
                    </span>
                    <span className="text-muted-foreground text-xs ml-1">
                      /{plan.period.replace('1 bulan', '1 Month').replace('bulan', 'Months')}
                    </span>
                  </div>

                  {(plan.id === '3_months' || plan.id === '6_months' || plan.id === '12_months') && (
                    <div className="mx-auto mt-2 mb-1 w-fit overflow-hidden rounded-sm shadow-md border border-[#408dbd]">
                      <div className="bg-gradient-to-b from-[#358bc2] to-[#125887] py-1.5 px-4 text-center relative border-t border-t-[#66ade0] border-l border-l-[#66ade0] shadow-inner">
                        {/* Folded corner effect (bottom right silver) */}
                        <div className="absolute bottom-0 right-0 w-0 h-0 border-b-[16px] border-l-[16px] border-b-[#e2e8f0] border-l-transparent drop-shadow-[-1px_-1px_1px_rgba(0,0,0,0.4)]"></div>
                        
                        <div className="text-[12px] font-black text-[#fde047] uppercase tracking-wide leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                          FREE {plan.id === '3_months' ? 1 : plan.id === '6_months' ? 2 : 3} MONTH{plan.id === '3_months' ? '' : 'S'}
                        </div>
                        <div className="text-[10px] font-bold text-white uppercase tracking-wider mb-1 leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                          LIVE SCREENER ALERT
                        </div>
                        <div className="flex items-center justify-center gap-1 text-[9px] font-medium text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                          <div className="bg-[#2AABEE] rounded-full p-0.5 inline-flex items-center justify-center">
                            <Send className="w-2.5 h-2.5 text-white -ml-0.5" />
                          </div>
                          Via Telegram
                        </div>
                      </div>
                    </div>
                  )}
                </CardHeader>

                <CardContent className="px-3 pb-3 pt-0 mt-auto w-full flex flex-col justify-end">
                  <div className="pt-1">

                    {(() => {
                      const subStatus = getSubscriptionStatus();
                      const isCurrentPlan = subStatus.isActive &&
                        subStatus.planId === plan.id;

                      if (isCurrentPlan && !subStatus.isCancelled) {
                        return (
                          <Button
                            className="w-full h-8 text-xs"
                            disabled
                            variant="default"
                            size="sm"
                          >
                            <CheckCircle className="mr-2 h-3 w-3" />
                            Current Plan
                          </Button>
                        );
                      }

                      if (!isReleased) {
                        return (
                          <Button
                            className="w-full h-8 text-xs font-medium tracking-tight bg-slate-800 text-white hover:bg-slate-700 border border-slate-700 opacity-90"
                            onClick={(e) => {
                              // Easter egg bypass to help with testing without restarting Vite
                              if (e.detail === 5) {
                                setIsReleased(true);
                                toast.success("Test Mode diaktifkan lewat rahasia 5-klik!");
                                return;
                              }
                              toast.info('Subscription feature is currently locked. Stay tuned for the launch on Monday, 29 June 2026 at 09:00 WIB!');
                            }}
                            size="sm"
                          >
                            <Clock className="w-3.5 h-3.5 mr-1.5" />
                            {releaseTimeLeft}
                          </Button>
                        );
                      }

                      let buttonText = 'Subscribe Now';
                      if (subStatus.isActive) {
                        buttonText = (subStatus.isCancelled && isCurrentPlan) ? 'Renew Plan' : 'Change Plan';
                      }

                      return (
                        <Button
                          className={`w-full h-8 text-xs font-semibold transition-all duration-300 ${plan.popular
                            ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm hover:shadow-md'
                            : 'bg-background border border-primary text-primary hover:bg-accent hover:text-white dark:hover:bg-accent dark:hover:text-white'
                            }`}
                          variant={plan.popular ? "default" : "outline"}
                          onClick={() => {
                            if (subStatus.isActive) {
                              setSelectedChangePlan(plan);
                              setShowChangePlanDialog(true);
                            } else {
                              handleSubscribe(plan.id);
                            }
                          }}
                          disabled={loading || isFetchingStatus || hasActivePayment || loadingPlan === plan.id}
                          size="sm"
                        >
                          {isFetchingStatus ? (
                            <>
                              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                              Loading...
                            </>
                          ) : loadingPlan === plan.id ? (
                            <>
                              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            buttonText
                          )}
                        </Button>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>


        </CardContent>
      </Card>


      {/* Payment Methods */}
      <Card className="!gap-1">
        <CardHeader className="pt-2 pb-0 px-3">
          <CardTitle className="text-sm">Payment Methods</CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-2 pt-0">
          <div className="overflow-x-auto">
            <div className="flex gap-3 pb-0 mx-auto" style={{ width: 'max-content' }}>
              {Object.values(paymentMethodsConfig).map((group, index) => (
                <PaymentGroup
                  key={index}
                  title={group.title}
                  logos={group.logos}
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>



      {/* FAQ */}
      <Card className="!gap-1">
        <CardContent className="px-3 pt-2 pb-2">
          <h3 className="text-sm font-semibold mb-3 ml-1">Additional Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="bg-muted/30 p-2 rounded-lg border border-border">
              <h4 className="font-medium text-sm">Can I change my plan?</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated automatically.
              </p>
            </div>
            <div className="bg-muted/30 p-2 rounded-lg border border-border">
              <h4 className="font-medium text-sm">What happens if I cancel?</h4>
              <p className="text-xs text-muted-foreground mt-1">
                You'll continue to have full access to all features until the end of your current billing period.
              </p>
            </div>
            <div className="bg-muted/30 p-2 rounded-lg border border-border">
              <h4 className="font-medium text-sm">Are there hidden fees?</h4>
              <p className="text-xs text-muted-foreground mt-1">
                No, the prices shown are final. You will not be charged any additional or hidden fees.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Cancellation
            </DialogTitle>
            <DialogDescription className="text-left space-y-3 pt-4">
              <p>Are you sure you want to cancel your subscription?</p>
              <p className="text-sm text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded">
                <strong>Note:</strong> Your plan will remain active until {subscriptionStatus?.subscription?.end_date ? new Date(subscriptionStatus.subscription.end_date).toLocaleDateString('id-ID') : 'the end of your billing period'}, then it will automatically downgrade to Free plan.
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Type <strong>"Cancel"</strong> to confirm:
                </label>
                <Input
                  type="text"
                  value={cancelConfirmText}
                  onChange={(e) => setCancelConfirmText(e.target.value)}
                  placeholder="Cancel"
                  className="w-full"
                />
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCancelDialog(false);
                setCancelConfirmText('');
              }}
            >
              Keep Subscription
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={loading || cancelConfirmText !== 'Cancel'}
            >
              {loading ? 'Cancelling...' : 'Confirm Cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Confirmation Dialog */}
      <Dialog open={showChangePlanDialog} onOpenChange={setShowChangePlanDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary">
              <AlertCircle className="h-5 w-5" />
              Confirm Plan Change
            </DialogTitle>
            <DialogDescription className="text-left space-y-3 pt-4 text-foreground">
              <p>Are you sure you want to subscribe to <strong>{selectedChangePlan?.name}</strong>?</p>
              <p className="text-sm bg-primary/10 p-3 rounded text-primary">
                <strong>Note:</strong> Your current plan (<strong>{getSubscriptionStatus().planName}</strong>) still has <strong>{formatDuration(getSubscriptionStatus().daysLeft)}</strong> left. This remaining duration will be automatically added to your new plan!
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowChangePlanDialog(false);
                setSelectedChangePlan(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowChangePlanDialog(false);
                if (selectedChangePlan) {
                  handleSubscribe(selectedChangePlan.id);
                }
              }}
              disabled={loading}
            >
              Proceed to Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
